/*
 * <order-lookup> — 비회원 주문 조회 폼.
 * 원본 Hydrogen 라우트 app/routes/($locale).help.order-lookup.tsx 의 클라이언트 쪽을 옮긴 것.
 * 화면 구조는 sections/main-order-lookup.liquid 머리말에 정리돼 있다.
 *
 * 하는 일은 셋이다.
 *   1. 휴대폰 / 이메일 탭 전환 (원본에는 없다 — 이 화면 디자인이 두 갈래를 준다)
 *   2. 제출 전 검증. 원본의 react-hook-form rules 를 그대로 옮겼다.
 *   3. POST → 성공하면 주문 상태 페이지로 이동, 실패하면 폼 아래 문구.
 *      주문 상세는 Shopify 주문 상태 페이지가 그리므로 여기서 렌더링하는 것은 없다.
 *
 * 원본은 실패를 토스트로 알리는데 테마엔 토스트가 없어서, 필드 오류와 같은 모양의
 * 폼 단위 문구로 바꿨다 — Figma 도 이 화면의 피드백을 문구로 그린다.
 *
 * ── 조회 API (kihnoframe WEB STORE, operationId=guestOrderLookup)
 *   POST {base}/store/shopify/guest/order/lookup
 *     { orderIdentifier, contact, method: "phone"|"email", storeRegion: "kr"|"us"|"jp" }
 *   200 { result: true,  data: { orderName, statusPageUrl } }
 *       { result: false, status_code: 403, errorCode, error_msg }
 *   Admin API 호출과 본인확인은 그쪽이 한다 — 이 파일은 형식 검증까지만 하고
 *   권한 판정은 하지 않는다. 스펙: /v3/api-docs/WEB%20STORE
 *
 * 베이스 URL 은 호스트명으로 갈린다(script-gnb-user-type.js / script-track-list.js 와 같은 방식).
 * 테마 레코드마다 설정을 따로 넣어줄 필요가 없도록 — 실서비스 테마가 테스트 서버를 때리는
 * 사고를 설정 누락으로 만들지 않으려는 것.
 */
const ORDER_LOOKUP_RELEASE_BASE_URL = 'https://wldv2.kihnoframe.com';
const ORDER_LOOKUP_TEST_BASE_URL = 'https://testv2.kihnoframe.com';
const ORDER_LOOKUP_PATH = '/store/shopify/guest/order/lookup';

if (!customElements.get('order-lookup')) {
  customElements.define(
    'order-lookup',
    class OrderLookup extends HTMLElement {
      constructor() {
        super();

        // 원본 app/constants/signEvent.ts 와 같은 정규식.
        this.EMAIL_REGEX = /^([\w.%+-]+)@([\w-]+\.)+([\w]{2,})$/i;
        this.PHONE_NUMBER_REGEX = /^[0-9]+$/;

        const { hostname } = window.location;
        const isProduction = hostname === 'kitbetter.com' || hostname.endsWith('.kitbetter.com');
        const base = (this.dataset.apiBase || (isProduction ? ORDER_LOOKUP_RELEASE_BASE_URL : ORDER_LOOKUP_TEST_BASE_URL))
          .trim()
          .replace(/\/+$/, '');
        this.endpoint = base + ORDER_LOOKUP_PATH;
        // 주문이 발생한 스토어. 이 테마는 EN/US 스토어라 기본이 us — API 기본값(kr)과 다르므로
        // 항상 명시해서 보낸다.
        this.storeRegion = (this.dataset.storeRegion || 'us').trim();
        this.strings = this.parseStrings();

        this.form = this.querySelector('.order-lookup__form');
        this.tabs = Array.from(this.querySelectorAll('[data-tab]'));
        this.panels = Array.from(this.querySelectorAll('[data-panel]'));
        this.submitButton = this.querySelector('.order-lookup__submit');
        this.submitSpinner = this.submitButton && this.submitButton.querySelector('.loading__spinner');
        this.formError = this.querySelector('[data-error-for="form"]');

        // Liquid 이 그려 둔 활성 탭이 그대로 시작 상태다. JS 는 전환만 맡는다.
        // 갈래가 하나뿐이면 탭 스트립이 아예 없으므로 data-method 가 시작 상태를 말한다.
        const active = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true');
        this.method = (active && active.dataset.tab) || this.dataset.method || 'email';

        this.isBusy = false;
      }

      connectedCallback() {
        if (!this.form) return;

        this.tabs.forEach((tab) => {
          tab.addEventListener('click', () => this.selectMethod(tab.dataset.tab));
          // role="tab" 을 붙였으니 좌우 화살표로도 옮겨 다녀야 한다. 탭 두 개 모두 Tab
          // 순서에 남겨 두고(roving tabindex 없이) 화살표만 더한다 — 폼이 바로 아래
          // 있어서, 활성 탭 하나만 Tab 으로 닿게 만들 이유가 없다.
          tab.addEventListener('keydown', (event) => this.onTabKeydown(event));
        });

        this.form.addEventListener('submit', (event) => {
          event.preventDefault();
          this.submit();
        });

        // 입력을 다시 시작하면 그 필드의 오류만 지운다.
        this.querySelectorAll('[data-field]').forEach((field) => {
          field.addEventListener('input', () => this.clearFieldError(field.dataset.field));
        });
      }

      /*
       * Liquid 의 `t` 필터는 출력을 HTML 이스케이프하고 <script> 본문은 HTML 파서가
       * 엔티티를 되돌리지 않는 raw text 라, 여기서 직접 풀어 준다 (응모폼 / 트랙리스트와 동일).
       */
      parseStrings() {
        try {
          const raw = JSON.parse(this.querySelector('[data-order-lookup-strings]').textContent);
          const decoder = document.createElement('textarea');
          const decode = (value) => {
            decoder.innerHTML = value;
            return decoder.value;
          };
          return Object.keys(raw).reduce((acc, key) => {
            acc[key] = typeof raw[key] === 'string' ? decode(raw[key]) : raw[key];
            return acc;
          }, {});
        } catch (error) {
          return {};
        }
      }

      onTabKeydown(event) {
        const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
        if (!step) return;

        event.preventDefault();
        const from = this.tabs.indexOf(event.currentTarget);
        const next = this.tabs[(from + step + this.tabs.length) % this.tabs.length];
        // 화살표로 옮기면 그 자리에서 바로 전환되는 게 이 패턴의 기본 동작이다.
        next.focus();
        this.selectMethod(next.dataset.tab);
      }

      selectMethod(method) {
        if (!method || method === this.method) return;
        this.method = method;

        this.tabs.forEach((tab) => {
          const isActive = tab.dataset.tab === method;
          tab.classList.toggle('kit-tabs__item--active', isActive);
          tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });

        this.panels.forEach((panel) => {
          panel.hidden = panel.dataset.panel !== method;
        });

        // 갈래를 바꾸면 이전 갈래에서 받은 피드백은 더 이상 이 폼의 상태가 아니다.
        // 주문번호는 두 갈래가 함께 쓰는 필드라 그 오류만 남긴다.
        this.clearFieldError('phoneNum');
        this.clearFieldError('email');
        this.setFormError('');
        // 포커스는 누른 탭에 그대로 둔다 — 탭 활성화가 포커스를 패널로 끌고 가지 않는 것이
        // ARIA 탭 패턴의 기본이고, 화살표 이동과도 부딪히지 않는다.
      }

      field(name) {
        return this.querySelector(`[data-field="${name}"]`);
      }

      value(name) {
        const el = this.field(name);
        return el ? el.value.trim() : '';
      }

      setFieldError(name, message) {
        const errorEl = this.querySelector(`[data-error-for="${name}"]`);
        if (errorEl) {
          errorEl.textContent = message;
          errorEl.hidden = false;
        }
        const box = this.querySelector(`[data-box-for="${name}"]`);
        if (box) box.classList.add('order-lookup__input-box--error');
      }

      clearFieldError(name) {
        const errorEl = this.querySelector(`[data-error-for="${name}"]`);
        if (errorEl) {
          errorEl.textContent = '';
          errorEl.hidden = true;
        }
        const box = this.querySelector(`[data-box-for="${name}"]`);
        if (box) box.classList.remove('order-lookup__input-box--error');
      }

      setFormError(message) {
        if (!this.formError) return;
        this.formError.textContent = message;
        this.formError.hidden = !message;
      }

      /* 원본 useForm rules 와 같은 순서·같은 문구. 첫 오류 필드로 포커스를 옮긴다. */
      validate() {
        const invalid = [];
        const fail = (name, message) => {
          this.setFieldError(name, message);
          invalid.push(name);
        };

        this.clearFieldError('orderIdentifier');
        this.clearFieldError(this.method === 'phone' ? 'phoneNum' : 'email');

        if (!this.orderIdentifier()) fail('orderIdentifier', this.strings.orderNumberRequired);

        if (this.method === 'phone') {
          const phone = this.value('phoneNum');
          if (!phone) fail('phoneNum', this.strings.phoneRequired);
          else if (!this.PHONE_NUMBER_REGEX.test(phone)) fail('phoneNum', this.strings.phoneForm);
        } else {
          const email = this.value('email');
          if (!email) fail('email', this.strings.emailRequired);
          else if (!this.EMAIL_REGEX.test(email)) fail('email', this.strings.emailForm);
        }

        if (invalid.length > 0) {
          const first = this.field(invalid[0]);
          if (first) first.focus();
        }
        return invalid.length === 0;
      }

      /* 원본 onSubmit / action 과 같은 정규화 — 앞의 '#' 을 떼고 trim. */
      orderIdentifier() {
        return this.value('orderIdentifier').replace(/^#/, '').trim();
      }

      /*
       * 대조에 쓸 연락처 한 값. 이메일은 소문자로(원본 action 의 toLowerCase 와 짝),
       * 휴대폰은 E.164 — 국가번호 + 국내 접두 0 을 뗀 번호. Shopify 가 order.phone 을
       * 그 꼴로 저장하기 때문이다.
       */
      contact() {
        if (this.method === 'email') return this.value('email').toLowerCase();

        const select = this.field('phoneCode');
        const code = ((select && select.value) || '').split('_')[0];
        const digits = this.value('phoneNum').replace(/^0+/, '');
        return `+${code}${digits}`;
      }

      async submit() {
        if (this.isBusy) return;
        this.setFormError('');
        if (!this.validate()) return;

        this.setBusy(true);

        try {
          const response = await fetch(this.endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              orderIdentifier: this.orderIdentifier(),
              contact: this.contact(),
              method: this.method,
              storeRegion: this.storeRegion,
            }),
          });

          const payload = await response.json().catch(() => ({}));
          // 성공 판정은 statusPageUrl 유무로 한다. 스펙은 실패도 HTTP 200 + result:false 로
          // 온다고 하는데 실제 구현은 404 를 쓰기도 해서(아래 errorMessage 주석), HTTP 상태와
          // result 중 어느 쪽도 단독으로는 믿을 수 없다.
          const data = payload && payload.data;
          const statusUrl = data && payload.result !== false && data.statusPageUrl;

          if (statusUrl) {
            // Reference: app/routes/($locale).help.order-lookup.tsx:125 fires this on the
            // successful lookup, just before the same redirect. It hardcodes
            // {method: 'email'} because email was the only path it had; this theme also
            // looks up by phone, so the real method is sent under the same key.
            window.GTM?.viewGuestOrderDetail({ method: this.method });

            // 성공하면 Shopify 주문 상태 페이지로 넘긴다(원본과 같음). 이동 중에도
            // 스피너는 계속 돌아야 하므로 setBusy(false) 를 하지 않는다.
            setTimeout(() => {
              window.location.href = statusUrl;
            }, 150);
            return;
          }

          this.setBusy(false);
          this.setFormError(this.errorMessage(payload, response.status));
        } catch (error) {
          this.setBusy(false);
          this.setFormError(this.strings.GUEST_ORDER_LOOK_UP_ERROR);
        }
      }

      /*
       * 백엔드 실패 응답을 문구로. 2026-08-27 testv2 확인 기준.
       *
       * 조회 실패(주문 없음 · 본인확인 실패)는 HTTP 200 으로 오고 보여줄 문구가 error_msg 에
       * 담겨 온다. 이 경우는 서버 문구를 그대로 노출한다 — 어떤 이유로 못 찾았는지는 서버만
       * 알기 때문이다.
       *   200 { result:false, status_code:200, error_msg:"Order not found" }
       *
       * 반대로 HTTP 200 이 아닌 실패의 error_msg 는 구매자용 문구가 아니다. 실제로 이렇게 온다:
       *   400 { error_msg:"공백일 수 없습니다" }        ← 프레임워크 검증 문구(한국어 고정)
       *   502 { error_msg:"External api call failed" } ← 내부 오류
       * 영문 스토어에 한국어가 나가거나 내부 사정이 노출되므로 테마의 로케일 문구로 바꿔 말한다.
       * (400 은 테마가 먼저 형식 검증을 하므로 정상 흐름에서는 도달하지 않는다.)
       *
       * errorCode 는 아직 늘 null 인데, 값 목록을 받으면 strings 에 그 키만 추가하면
       * 이유별 문구로 갈라진다 — 그때는 이 분기가 가장 먼저 걸린다.
       */
      errorMessage(payload, httpStatus) {
        const code = (payload && (payload.errorCode || payload.code)) || '';
        if (code && this.strings[code]) return this.strings[code];

        if (httpStatus === 200) {
          const serverMessage = payload && typeof payload.error_msg === 'string' && payload.error_msg.trim();
          return serverMessage || this.strings.ORDER_NOT_FOUND;
        }

        return this.strings.GUEST_ORDER_LOOK_UP_ERROR;
      }

      setBusy(busy) {
        this.isBusy = busy;
        if (this.submitButton) {
          this.submitButton.classList.toggle('loading', busy);
          if (busy) this.submitButton.setAttribute('aria-disabled', 'true');
          else this.submitButton.removeAttribute('aria-disabled');
        }
        if (this.submitSpinner) this.submitSpinner.classList.toggle('hidden', !busy);
      }
    }
  );
}
